<!--
  Button — reusable button component.

  Props:
    variant — appearance:
      'primary'   — dark blue background, white text (default)
      'secondary' — light blue background, dark blue text, Poppins
      'bordered'  — light blue background, dark blue text, thin border, Inter
      'light'     — light blue background, brand blue text, Inter 16
      'white'     — white background, dark blue text, Poppins (on dark banners)
    size — height:
      'sm' — 40px (min-h-10)
      'md' — 48px (min-h-12, default)
    radius — corner rounding:
      'pill' — fully rounded (default; the inherited B2C look)
      'md'   — 8px, the B2B design's button radius
    as — override the rendered tag when `to` is not set (e.g. 'span' for a
      button-looking element nested inside a link)
    to — if provided, renders as <NuxtLinkLocale> instead of <button>
    localized — set false when `to` already carries a locale prefix (e.g. a CMS-supplied
      URL run through relativeUrl()); renders a plain <NuxtLink> so the locale
      is not prepended twice

  All other attributes (disabled, type, aria-*, event listeners, etc.)
  are passed through to the root element via v-bind="$attrs".

  Usage:
    <Button>Susisiekti</Button>
    <Button variant="secondary" size="sm">Susisiekti</Button>
    <Button variant="bordered" to="/katalogas">Sužinoti daugiau</Button>
    <Button :to="relativeUrl(cmsUrl)" :localized="false">Atrask daugiau</Button>
    <Button><BarsIcon class="w-[10px] h-auto" /> Susisiekti</Button>
-->
<template>
  <component
    :is="component"
    :to="to"
    v-bind="$attrs"
    class="inline-flex py-1 justify-center items-center gap-4 transition-colors text-center disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed"
    :class="[variantClasses[variant], sizeClasses[size], radiusClasses[radius]]"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
// NuxtLink must be imported: resolveComponent('NuxtLink') does not resolve it and
// silently renders a literal <NuxtLink> custom element instead of an anchor.
import { NuxtLink } from '#components'

defineOptions({ inheritAttrs: false })

const props = withDefaults(defineProps<{
  variant?: 'primary' | 'secondary' | 'bordered' | 'light' | 'white'
  size?: 'sm' | 'md'
  radius?: 'pill' | 'md'
  as?: string
  to?: string | Record<string, unknown>
  localized?: boolean
}>(), {
  variant: 'primary',
  size: 'md',
  radius: 'pill',
  as: 'button',
  localized: true,
})

const variantClasses: Record<string, string> = {
  primary: 'px-6.25 bg-brand-dark-blue hover:bg-[#0064BC] font-poppins text-15 text-white font-semibold leading-5',
  secondary: 'px-6.25 bg-brand-light-blue hover:bg-brand-light-blue/70 font-poppins text-15 text-brand-dark-blue font-semibold leading-5',
  bordered: 'border border-[#809EB8] px-3 bg-brand-light-blue hover:bg-brand-light-grey font-inter text-15 text-brand-dark-blue font-normal leading-4.5',
  light: 'px-6.25 bg-brand-light-blue hover:bg-brand-light-blue/70 font-inter text-base text-brand-blue font-semibold leading-4.5',
  white: 'px-6.25 bg-white hover:bg-brand-light-blue font-poppins text-15 text-brand-dark-blue font-semibold leading-5',
}

const sizeClasses: Record<string, string> = {
  sm: 'min-h-8',
  md: 'min-h-12',
}

const radiusClasses: Record<string, string> = {
  pill: 'rounded-4xl',
  md: 'rounded-lg',
}

const component = computed(() => props.to
  ? (props.localized ? resolveComponent('NuxtLinkLocale') : NuxtLink)
  : props.as)
</script>
